import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Popup, usePopups } from '../api/liveContent';

/** Pages where a popup would get in the way of someone filling in a form. */
const FORM_PAGES = ['/contact-us', '/open-account', '/customer-survey'];

const KEY = (p: Popup) => `mybank-popup:${p.id}:${p.updatedAt}`;

function read(store: Storage | null, key: string): number | null {
  try {
    const v = store?.getItem(key);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}
function write(store: Storage | null, key: string) {
  try {
    store?.setItem(key, String(Date.now()));
  } catch {
    /* private mode: it can show again on the next page load */
  }
}

const local = () => (typeof window === 'undefined' ? null : window.localStorage);
const session = () => (typeof window === 'undefined' ? null : window.sessionStorage);

/** The Somali site lives under /so; the rules in the ERP are written for the plain path. */
const plainPath = (pathname: string) => {
  const p = pathname.replace(/^\/so(?=\/|$)/, '') || '/';
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
};

function pathMatches(paths: string[], path: string) {
  return paths.some((p) => {
    if (p.endsWith('*')) return path.startsWith(p.slice(0, -1));
    return p === '/' ? path === '/' : path === p || path === `${p}/`;
  });
}

function allowedOnPage(p: Popup, path: string) {
  const { mode, paths } = p.display.pages;
  if (mode === 'include') return pathMatches(paths, path);
  if (mode === 'exclude') return !pathMatches(paths, path);
  return true;
}

/** Frequency rule, from what this browser has already seen. Edited popups get a new key, so they show again. */
function frequencyAllows(p: Popup) {
  const { frequency, frequencyDays } = p.display;
  if (frequency === 'every_visit') return true;
  if (frequency === 'once_per_session') return read(session(), KEY(p)) === null;
  const seen = read(local(), KEY(p));
  if (seen === null) return true;
  if (frequency === 'once_ever') return false;
  return Date.now() - seen >= frequencyDays * 86_400_000;
}

const isTyping = () => {
  const el = document.activeElement as HTMLElement | null;
  return Boolean(el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable));
};

/**
 * One popup at a time: the highest-priority live popup that passes its page, device and
 * frequency rules. Renders nothing until its trigger fires. Uses the native <dialog>
 * (modal), which gives the focus trap, Escape, an inert page behind it and focus
 * restore on close. Shows nothing while loading, on error, or when nothing is live.
 */
const PopupHost: React.FC = () => {
  const { data } = usePopups();
  const { pathname } = useLocation();
  const path = plainPath(pathname);
  const [open, setOpen] = useState<Popup | null>(null);
  // Popups closed on this page view; "every visit" popups must not reopen right after closing.
  const closed = useRef(new Set<string>());

  useEffect(() => {
    const popups = data ?? [];
    if (!popups.length || FORM_PAGES.includes(path)) return;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const candidate = popups.find(
      (p) =>
        !closed.current.has(p.id) &&
        p.display.devices.includes(mobile ? 'mobile' : 'desktop') &&
        allowedOnPage(p, path) &&
        frequencyAllows(p),
    );
    if (!candidate) return;

    let retry: number | undefined;
    const show = () => {
      // Never on the 404 page, and never over a field someone is typing in.
      if (document.querySelector('[data-no-popup]')) return;
      if (isTyping()) {
        retry = window.setTimeout(show, 1000);
        return;
      }
      write(candidate.display.frequency === 'once_per_session' ? session() : local(), KEY(candidate));
      setOpen(candidate);
    };

    const { trigger, delaySeconds, scrollPercent } = candidate.display;
    const cleanups: (() => void)[] = [() => window.clearTimeout(retry)];
    // Exit intent needs a mouse; touch devices use the delay instead.
    const useExit = trigger === 'exit_intent' && !mobile && window.matchMedia('(hover: hover)').matches;
    if (trigger === 'load') {
      show();
    } else if (trigger === 'scroll') {
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && (window.scrollY / max) * 100 >= scrollPercent) {
          window.removeEventListener('scroll', onScroll);
          show();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));
    } else if (useExit) {
      const onOut = (e: MouseEvent) => {
        if (e.clientY <= 0 && !e.relatedTarget) {
          document.removeEventListener('mouseout', onOut);
          show();
        }
      };
      document.addEventListener('mouseout', onOut);
      cleanups.push(() => document.removeEventListener('mouseout', onOut));
    } else {
      const t = window.setTimeout(show, delaySeconds * 1000);
      cleanups.push(() => window.clearTimeout(t));
    }
    return () => cleanups.forEach((c) => c());
  }, [data, path]);

  const close = useCallback(() => {
    setOpen((p) => {
      if (p) closed.current.add(p.id);
      return null;
    });
  }, []);

  return open ? <PopupDialog popup={open} onClose={close} /> : null;
};

const btnPrimary =
  'inline-flex min-h-[48px] items-center justify-center rounded-full bg-[#017EFF] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#0165CC]';
const btnSecondary =
  'inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#001F5F]/30 px-6 text-sm font-semibold text-[#001F5F] transition-colors hover:border-[#001F5F] hover:bg-[#EFF5FF]';

const PopupButton: React.FC<{ to: string; className: string; onClick: () => void; children: React.ReactNode }> = ({
  to,
  className,
  onClick,
  children,
}) =>
  /^https?:/i.test(to) ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  ) : (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );

const PopupDialog: React.FC<{ popup: Popup; onClose: () => void }> = ({ popup, onClose }) => {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = `popup-title-${popup.id}`;
  const bodyId = `popup-body-${popup.id}`;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    dialog.showModal();
    // The page behind must not scroll while the modal is open.
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = previous;
      if (dialog.open) dialog.close();
    };
  }, []);

  const { image, layout } = popup;

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={popup.body ? bodyId : undefined}
      // Strict Mode re-runs the effect above and its cleanup fires a stale 'close' event; only act if it is really closed.
      onClose={() => {
        if (!ref.current?.open) onClose();
      }}
      onClick={(e) => {
        if (popup.closeOnBackdrop && e.target === e.currentTarget) e.currentTarget.close();
      }}
      className="m-auto max-h-[calc(100dvh-1.5rem)] w-[min(54rem,calc(100vw-1.5rem))] overflow-auto rounded-2xl border-0 bg-white p-0 text-[#0B1A36] shadow-2xl backdrop:bg-[#00143F]/70"
    >
      <div className={`relative flex flex-col ${layout === 'image_right' ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
        <button
          type="button"
          autoFocus
          aria-label="Close"
          onClick={(e) => e.currentTarget.closest('dialog')?.close()}
          className="absolute right-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-[#001F5F] shadow hover:bg-[#EFF5FF]"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        {image ? (
          <div className="relative h-52 shrink-0 bg-[#EFF5FF] md:h-auto md:min-h-[26rem] md:w-1/2">
            <img
              src={image.url}
              srcSet={`${image.thumbUrl} 800w, ${image.url} ${image.width}w`}
              sizes="(min-width: 768px) 432px, 100vw"
              alt={image.alt}
              width={image.width}
              height={image.height}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        ) : null}

        <div className="flex flex-1 flex-col justify-center gap-4 p-6 pr-14 sm:p-8 md:pr-8">
          {popup.label ? (
            <p className="text-sm font-semibold uppercase tracking-wide text-[#0165CC]">{popup.label}</p>
          ) : null}
          <h2 id={titleId} className="text-2xl font-bold leading-tight text-[#001F5F] sm:text-3xl">
            {popup.title}
          </h2>
          {popup.body ? (
            <p id={bodyId} className="whitespace-pre-line text-base text-slate-600 sm:text-lg">
              {popup.body}
            </p>
          ) : null}
          {popup.primary ? (
            <div className="mt-2 flex flex-wrap gap-3">
              <PopupButton to={popup.primary.url} className={btnPrimary} onClick={onClose}>
                {popup.primary.label}
              </PopupButton>
              {popup.secondary ? (
                <PopupButton to={popup.secondary.url} className={btnSecondary} onClick={onClose}>
                  {popup.secondary.label}
                </PopupButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </dialog>
  );
};

export default PopupHost;
